/**
 * Entity Name Helpers
 * Derives display names from ID references
 *
 * These helpers derive display names from ID fields for consistent data access.
 */

import type { Unit, Tenant, Vendor, Property, Receivable, Bill } from '@repo/shared-types';
import { getUnitDisplayString } from '@repo/shared-utils';

/**
 * Get unit display string from unitId + propertyId (e.g. "A", "001", or "—" for Single Family)
 */
export function getUnitDisplayFromIds(
  unitId: string | undefined,
  propertyId: string,
  units: Unit[],
  properties: Property[]
): string {
  if (!unitId) return '—';
  const unit = units.find(u => u.id === unitId);
  if (!unit) return '—';
  const property = properties.find(p => p.id === propertyId);
  if (!property) return unit.unitNumber ?? 'Unknown';
  const s = getUnitDisplayString(unit.id, property.type, false);
  return s || (unit.unitNumber ?? '—');
}

/**
 * Get unit display string for a tenant (e.g. "A", "001", or "—" for Single Family)
 */
export function getTenantUnitDisplay(tenant: Tenant, units: Unit[], properties: Property[]): string {
  const apiUnitNumber = (tenant as Tenant & { unitNumber?: string }).unitNumber?.trim();
  if (apiUnitNumber) return apiUnitNumber;
  return getUnitDisplayFromIds(tenant.unitId, tenant.propertyId, units, properties);
}

/**
 * Get tenant name from unit using tenantId
 */
export function getUnitTenantName(
  unit: Unit,
  tenants: Tenant[]
): string | null {
  if (unit.tenantId) {
    const tenant = tenants.find(t => t.id === unit.tenantId);
    return tenant?.name || null;
  }
  return null;
}

/**
 * Get unit display string from receivable using unitId
 */
export function getReceivableUnitDisplay(
  receivable: Receivable,
  units: Unit[],
  properties: Property[]
): string {
  if (receivable.unitId) {
    const unit = units.find(u => u.id === receivable.unitId);
    if (unit) {
      const property = properties.find(p => p.id === unit.propertyId);
      if (property) {
        return getUnitDisplayString(unit.id, property.type, false);
      }
    }
  }
  return 'Unknown';
}

/**
 * Get vendor name from bill using vendorId
 */
export function getBillVendorName(
  bill: Bill,
  vendors: Vendor[]
): string {
  if (bill.vendorId) {
    const vendor = vendors.find(v => v.id === bill.vendorId);
    return vendor?.name || 'Unknown Vendor';
  }
  return 'Unknown Vendor';
}

/**
 * Get property name from ID
 */
export function getPropertyName(
  propertyId: string,
  properties: Property[]
): string {
  const property = properties.find(p => p.id === propertyId);
  return property?.name || 'Unknown Property';
}

/**
 * Get tenant name from ID
 */
export function getTenantName(
  tenantId: string,
  tenants: Tenant[]
): string | null {
  const tenant = tenants.find(t => t.id === tenantId);
  return tenant?.name || null;
}

/**
 * Get vendor name from ID
 */
export function getVendorName(
  vendorId: string,
  vendors: Vendor[]
): string | null {
  const vendor = vendors.find(v => v.id === vendorId);
  return vendor?.name || null;
}
